import { useEffect, useState, useMemo, useCallback } from "react";
import userService from "../services/userService";
import { filterUsers } from "../utils";
import { FILTER_STATUS } from "../utils";

export function useUsersList() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  const [modalState, setModalState] = useState({
    showUserModal: false,
    editingUser: null,
    showDeleteModal: false,
    userToDelete: null,
    selectedUserId: null,
  });

  const [userTypeFilter, setUserTypeFilter] = useState(FILTER_STATUS.ALL);
  const [includeInactive, setIncludeInactive] = useState(false);

  // Load users on mount
  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await userService.getAll();
      setUsers(data || []);
    } catch (err) {
      setError(err.message || "Erreur lors du chargement des adhérents");
    } finally {
      setLoading(false);
    }
  }, []);

  // Memoize filtered users
  const filteredUsers = useMemo(
    () =>
      filterUsers(users, {
        userType: userTypeFilter,
        includeInactive,
      }),
    [users, userTypeFilter, includeInactive],
  );

  const toggleIncludeInactive = useCallback(
    () => setIncludeInactive((prev) => !prev),
    [],
  );

  // CRUD callbacks
  const handleCreate = useCallback(
    () =>
      setModalState((s) => ({ ...s, showUserModal: true, editingUser: null })),
    [],
  );

  const handleEdit = useCallback(
    (user) =>
      setModalState((s) => ({ ...s, showUserModal: true, editingUser: user })),
    [],
  );

  const handleViewDetails = useCallback(
    (userId) => setModalState((s) => ({ ...s, selectedUserId: userId })),
    [],
  );

  const handleDeleteClick = useCallback(
    (user) =>
      setModalState((s) => ({
        ...s,
        showDeleteModal: true,
        userToDelete: user,
      })),
    [],
  );

  const handleDelete = useCallback(async () => {
    const { userToDelete } = modalState;
    if (!userToDelete) return;

    try {
      await userService.delete(userToDelete.id);
      setSuccessMessage("Adhérent supprimé ");
      closeDeleteModal();
      setModalState((s) => ({ ...s, selectedUserId: null }));
      await loadUsers();
    } catch (err) {
      setError(err.message || "Erreur lors de la suppression");
      closeDeleteModal();
    }
  }, [modalState, loadUsers]);

  const handleFormSubmit = useCallback(
    async (userData) => {
      const { editingUser } = modalState;
      try {
        if (editingUser) {
          await userService.update(editingUser.id, userData);
          setSuccessMessage("Adhérent modifié avec succès");
        } else {
          await userService.create(userData);
          setSuccessMessage("Adhérent créé avec succès");
        }
        closeUserModal();
        await loadUsers();
      } catch (err) {
        const message = err.message || "Erreur lors de la sauvegarde";
        setError(message);
        throw new Error(message);
      }
    },
    [modalState, loadUsers],
  );

  const handleSuccess = useCallback(async () => {
    await loadUsers();
  }, [loadUsers]);

  // Modal helpers
  const closeUserModal = useCallback(
    () =>
      setModalState((s) => ({
        ...s,
        showUserModal: false,
        editingUser: null,
      })),
    [],
  );

  const closeDeleteModal = useCallback(
    () =>
      setModalState((s) => ({
        ...s,
        showDeleteModal: false,
        userToDelete: null,
      })),
    [],
  );

  const closeUserCard = useCallback(
    () => setModalState((s) => ({ ...s, selectedUserId: null })),
    [],
  );

  const clearSuccessMessage = useCallback(() => setSuccessMessage(""), []);
  const clearError = useCallback(() => setError(null), []);

  return {
    users,
    filteredUsers,
    loading,
    error,
    successMessage,

    modalState,
    userTypeFilter,
    includeInactive,
    toggleIncludeInactive,
    setUserTypeFilter,

    handleCreate,
    handleEdit,
    handleViewDetails,
    handleDeleteClick,
    handleDelete,
    handleFormSubmit,
    handleSuccess,

    closeUserModal,
    closeDeleteModal,
    closeUserCard,
    clearSuccessMessage,
    clearError,
  };
}
